import React, { useState } from "react";

import { ConfirmModal, ConfirmModalProps } from "@/components/organisms/ConfirmModal";

import useNotificationStore from "../store/notification.store";

export type NotificationConfirmProps = Omit<
  ConfirmModalProps,
  "accept" | "reject" | "children" | "isLoading" | "open"
> & {
  accept: (handler: NotificationConfirmHandler) => Promise<boolean | void> | boolean | void;
  reject?: (handler: NotificationConfirmHandler) => void;
  content: React.ReactNode;
};

export type NotificationConfirmUpdateProps = Partial<
  Omit<NotificationConfirmProps, "accept" | "reject">
>;

type NotificationConfirmHandler = {
  update: (props: NotificationConfirmUpdateProps) => void;
  close: () => void;
};

export function NotificationConfirm({
  content,
  accept,
  reject,
  ...props
}: NotificationConfirmProps) {
  const [isPending, setIsPending] = useState(false);
  const closeConfirm = useNotificationStore((state) => state.closeConfirm);
  const updateConfirm = useNotificationStore((state) => state.updateConfirm);

  const handler: NotificationConfirmHandler = {
    update: updateConfirm,
    close: closeConfirm,
  };

  const handlerOnAccept = () => {
    const response = accept(handler);
    if (response === false) {
      return;
    }

    if (!(response instanceof Promise)) {
      closeConfirm();
      return;
    }

    const promise = response;
    setIsPending(true);
    promise
      ?.then((response) => {
        if (response !== false) {
          closeConfirm();
        }
      })
      .finally(() => {
        setIsPending(false);
      });
  };

  const handlerOnReject = () => {
    reject?.(handler);
    closeConfirm();
  };

  return (
    <ConfirmModal
      accept={handlerOnAccept}
      isLoading={isPending}
      open={true}
      reject={handlerOnReject}
      {...props}
    >
      {content}
    </ConfirmModal>
  );
}
